// alova.ts
import { createAlova, Method } from "alova";
import ReactHook from "alova/react";
import customFetchAdapter from "./adapter";
import { encryptStorage } from "./storage";
import {
  getToken,
  getRefreshToken,
  setToken,
  setRefreshToken,
  clearAllTokens,
} from "./auth";

/* ==================== Types ==================== */

interface RetryableConfig {
  headers?: Record<string, string>;
  _retry?: boolean;
}

/* ==================== Env ==================== */

const baseURL = import.meta.env.VITE_URL_API as string;

/* ==================== Refresh Queue ==================== */

let isRefreshing = false;
let pendingQueue: Array<() => Promise<void>> = [];

/* ==================== Refresh Token ==================== */

async function refreshAccessToken(): Promise<string> {
  const refreshTokenValue = getRefreshToken();
  if (!refreshTokenValue) {
    throw new Error("No refresh token");
  }

  const res = await fetch(`${baseURL}/auth/refresh-token`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refresh_token: refreshTokenValue }),
  });

  if (!res.ok) {
    throw new Error("Refresh token failed");
  }

  const data = await res.json();

  setToken(data.data.access_token);
  setRefreshToken(data.data.refresh_token);

  return data.data.access_token;
}

/* ==================== Auth Alova ==================== */

export const authAlova = createAlova({
  baseURL,
  requestAdapter: customFetchAdapter,
  statesHook: ReactHook,
  responded: {
    async onSuccess(res: Response) {
      const clonedRes = res.clone();
      const data = await clonedRes.json();
      return data;
    },
  },
});

/* ==================== API Alova ==================== */

const apiResponded = {
  async onSuccess(res: Response, meta: any) {
    const clonedRes = res.clone();
    const data = await clonedRes.json();
    return data;
  },

  async onError(err: any, method: Method) {
    const config = method.config as RetryableConfig;

    if (err?.status === 401 && !config._retry) {
      config._retry = true;

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          pendingQueue.push(async () => {
            try {
              resolve(await method.send());
            } catch (e) {
              reject(e);
            }
          });
        });
      }

      try {
        isRefreshing = true;
        const newToken = await refreshAccessToken();

        pendingQueue.forEach((cb) => cb());
        pendingQueue = [];

        config.headers = {
          ...config.headers,
          Authorization: `Bearer ${newToken}`,
        };

        return await method.send();
      } catch (e) {
        clearAllTokens();
        window.location.href = "/";
        throw e;
      } finally {
        isRefreshing = false;
      }
    }

    throw err;
  },
};

export let apiAlova = createAlova({
  baseURL,
  requestAdapter: customFetchAdapter,
  statesHook: ReactHook,
  cacheFor: {
    GET: 3 * 60 * 1000, // 3 minutes
  },
  beforeRequest(method) {
    const token = getToken();
    if (token) {
      method.config.headers = {
        ...(method.config.headers as Record<string, string> | undefined),
        Authorization: `Bearer ${token}`,
      };
    }

    // Add selected role header for superadmin role switching
    const user = encryptStorage.getItem<Record<string, any>>("user");
    const activeRole = encryptStorage.getItem<string>("__active_role");
    if (user?.username === "superadmin" && activeRole && activeRole !== "__all__") {
      method.config.headers = {
        ...(method.config.headers as Record<string, string> | undefined),
        "X-Selected-Role": activeRole,
      };
    }
  },

  responded: apiResponded,
});

/* ==================== Reset ==================== */

export function resetApiAlova(): void {
  apiAlova = createAlova({
    baseURL,
    requestAdapter: customFetchAdapter,
    statesHook: ReactHook,

    beforeRequest(method) {
      const token = getToken();
      if (token) {
        method.config.headers = {
          ...(method.config.headers as Record<string, string> | undefined),
          Authorization: `Bearer ${token}`,
        };
      }
    },

    responded: apiResponded,
  });
}
